/**
 * TextBoundaryFinder - Text boundary utilities for word/sentence/paragraph selection
 * 
 * ✅ OPTION A IMPLEMENTATION
 * 
 * These utilities find text boundaries for multi-click selection.
 * They accept text as a parameter and should be called with authoritative text.
 */

// ============================================================================
// Types
// ============================================================================

export interface TextBoundary {
  start: number;
  end: number;
}

export interface TextContext {
  /** Characters before the position */
  before: string;
  /** Character at the position */
  at: string;
  /** Characters after the position */
  after: string;
  /** The position index */
  position: number;
}

// ============================================================================
// Word Boundaries
// ============================================================================

/**
 * Find word boundaries around a grapheme index.
 * Used for double-click word selection.
 *
 * Enhanced to handle:
 * - Contractions: "don't", "it's", "we're" (apostrophe within word)
 * - Hyphenated words: "self-aware", "well-known"
 * - Basic word characters: letters, numbers, underscore
 *
 * Matches MS Word behavior where double-click selects entire compound word.
 *
 * @param text - The authoritative document text
 * @param graphemeIndex - The grapheme index to find boundaries around
 * @returns Start and end positions of the word
 */
export function findWordBoundaries(text: string, graphemeIndex: number): TextBoundary {
  const index = Math.min(graphemeIndex, text.length - 1);

  if (index < 0 || text.length === 0) {
    return { start: 0, end: 0 };
  }

  // Basic word character: letters, numbers, underscore
  const isBasicWordChar = (char: string) => /[\w]/.test(char);

  // Check if character is a connector (apostrophe or hyphen) that joins words
  // Only counts as "part of word" if surrounded by word characters
  const isWordConnector = (char: string, pos: number): boolean => {
    if (char !== "'" && char !== '-' && char !== '\u2019') {
      return false;
    }
    // Must have word char before AND after to be a connector
    const hasBefore = pos > 0 && isBasicWordChar(text[pos - 1]);
    const hasAfter = pos < text.length - 1 && isBasicWordChar(text[pos + 1]);
    return hasBefore && hasAfter;
  };

  // Check if position is part of the word (either basic char or valid connector)
  const isPartOfWord = (pos: number): boolean => {
    if (pos < 0 || pos >= text.length) return false;
    const char = text[pos];
    return isBasicWordChar(char) || isWordConnector(char, pos);
  };

  // Find start of word
  let start = index;
  while (start > 0 && isPartOfWord(start - 1)) {
    start--;
  }

  // Find end of word
  let end = index;
  while (end < text.length && isPartOfWord(end)) {
    end++;
  }

  // If no word found, select the character
  if (start === end) {
    end = Math.min(index + 1, text.length);
  }

  return { start, end };
}

// ============================================================================
// Sentence Boundaries
// ============================================================================

/**
 * Find sentence boundaries around a grapheme index.
 * Used for triple-click sentence selection.
 * 
 * @param text - The authoritative document text
 * @param graphemeIndex - The grapheme index to find boundaries around
 * @returns Start and end positions of the sentence
 */
export function findSentenceBoundaries(text: string, graphemeIndex: number): TextBoundary {
  const index = Math.min(graphemeIndex, text.length - 1);
  
  if (index < 0 || text.length === 0) {
    return { start: 0, end: 0 };
  }
  
  // Sentence-ending punctuation
  const isSentenceEnd = (char: string) => /[.!?]/.test(char);
  
  // Find start of sentence (previous sentence-ending punctuation or start of paragraph)
  let start = index;
  while (start > 0) {
    const prevChar = text[start - 1];
    if (prevChar === '\n') {
      // Start of paragraph
      break;
    }
    if (isSentenceEnd(prevChar)) {
      // Found previous sentence end, move past it and any trailing spaces
      while (start < text.length && /\s/.test(text[start])) {
        start++;
      }
      break;
    }
    start--;
  }
  
  // Find end of sentence (next sentence-ending punctuation or end of paragraph)
  let end = index;
  while (end < text.length) {
    const currentChar = text[end];
    if (currentChar === '\n') {
      // End of paragraph
      break;
    }
    if (isSentenceEnd(currentChar)) {
      // Found sentence end, include the punctuation
      end++;
      // Include trailing spaces
      while (end < text.length && text[end] === ' ') {
        end++;
      }
      break;
    }
    end++;
  }
  
  return { start, end };
}

// ============================================================================
// Paragraph Boundaries
// ============================================================================

/**
 * Find paragraph boundaries around a grapheme index.
 * Used for paragraph-level selection.
 * 
 * @param text - The authoritative document text
 * @param graphemeIndex - The grapheme index to find boundaries around
 * @returns Start and end positions of the paragraph
 */
export function findParagraphBoundaries(text: string, graphemeIndex: number): TextBoundary {
  const index = Math.min(graphemeIndex, text.length - 1);
  
  if (index < 0 || text.length === 0) {
    return { start: 0, end: 0 };
  }
  
  // Find start of paragraph (previous \n or start of document)
  let start = index;
  while (start > 0 && text[start - 1] !== '\n') {
    start--;
  }
  
  // Find end of paragraph (next \n or end of document)
  let end = index;
  while (end < text.length && text[end] !== '\n') {
    end++;
  }
  
  // Include the trailing newline if present
  if (end < text.length && text[end] === '\n') {
    end++;
  }
  
  return { start, end };
}

// ============================================================================
// Text Context Utilities
// ============================================================================

/**
 * Get text context around a position for diagnostics.
 * 
 * @param text - The authoritative document text
 * @param position - The position to get context around
 * @param contextLength - Number of characters before/after to include (default: 5)
 * @returns Context object with before, at, and after text
 */
export function getTextContext(
  text: string,
  position: number,
  contextLength: number = 5
): TextContext {
  const start = Math.max(0, position - contextLength);
  const end = Math.min(text.length, position + contextLength + 1);
  
  return {
    before: text.slice(start, position),
    at: position < text.length ? text.charAt(position) : '',
    after: text.slice(position + 1, end),
    position
  };
}

/**
 * Get a human-readable description of the character at a position.
 * 
 * @param text - The authoritative document text
 * @param position - The position to describe
 * @returns Human-readable description
 */
export function describeCharacterAt(text: string, position: number): string {
  if (position < 0) return 'before-start';
  if (position >= text.length) return 'after-end';
  
  const char = text.charAt(position);
  
  switch (char) {
    case '\f': return 'FORM_FEED (page break)';
    case '\n': return 'NEWLINE';
    case '\r': return 'CARRIAGE_RETURN';
    case '\t': return 'TAB';
    case ' ': return 'SPACE';
    default:
      if (/[\w]/.test(char)) return `word-char '${char}'`;
      if (/[.!?]/.test(char)) return `sentence-end '${char}'`;
      if (/[,;:]/.test(char)) return `punctuation '${char}'`;
      return `'${char}'`;
  }
}
